import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import AGB from "./pages/AGB";
import IFU from "./pages/IFU";
import FAQPage from "./pages/FAQPage";
import TrackingSettings from "./pages/TrackingSettings";
import AdminDocuments from "./pages/AdminDocuments";
import AdminCalendar from "./pages/AdminCalendar";
import AdminPerformance from "./pages/AdminPerformance";
import AdminDashboard from "./pages/AdminDashboard";
import AdminVerarbeitungsverzeichnis from "./pages/AdminVerarbeitungsverzeichnis";
import AdminAuftragsverarbeitung from "./pages/AdminAuftragsverarbeitung";
import AdminBetroffenenrechte from "./pages/AdminBetroffenenrechte";
import AdminBackup from "./pages/AdminBackup";
import AdminISO27001 from "./pages/AdminISO27001";
import AdminChangelog from "./pages/AdminChangelog";
import AdminDownloads from "./pages/AdminDownloads";
import AdminRechtstexte from "./pages/AdminRechtstexte";
import AdminHandbuch from "./pages/AdminHandbuch";
import Auth from "./pages/Auth";
import AppointmentConfirmed from "./pages/AppointmentConfirmed";
import AIPress from "./pages/AIPress";
import Research from "./pages/Research";
import PatientInfo from "./pages/PatientInfo";
import AbutmentGuide from "./pages/AbutmentGuide";
import VisualExplainer from "./pages/VisualExplainer";
import OutboundRedirect from "./pages/OutboundRedirect";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import { ClinicalTrialBanner } from "@/components/ClinicalTrialBanner";
import { CookieConsent } from "@/components/CookieConsent";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <ClinicalTrialBanner />
          <CookieConsent />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/termin-bestaetigt" element={<AppointmentConfirmed />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/ifu" element={<IFU />} />
            {/* Single canonical AI/press URL: /ai-press is served as static HTML to every visitor.
                Former alternate URLs point there instead of duplicating the content. */}
            <Route path="/ai-press" element={<AIPress />} />
            <Route path="/ai-presse" element={<Navigate to="/ai-press" replace />} />
            <Route path="/research" element={<Research />} />
            <Route path="/fuer-patienten" element={<PatientInfo />} />
            <Route path="/eap-erklaert" element={<VisualExplainer />} />
            <Route path="/ti-base-oder-individuelles-abutment" element={<AbutmentGuide />} />
            <Route path="/abutment-guide" element={<Navigate to="/ti-base-oder-individuelles-abutment" replace />} />
            <Route path="/abutment-leitfaden" element={<Navigate to="/ti-base-oder-individuelles-abutment" replace />} />
            <Route path="/out" element={<OutboundRedirect />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/downloads"
              element={
                <ProtectedRoute>
                  <AdminDownloads />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/tracking"
              element={
                <ProtectedRoute>
                  <TrackingSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/kalender" 
              element={
                <ProtectedRoute>
                  <AdminCalendar />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/documents" 
              element={
                <ProtectedRoute>
                  <AdminDocuments />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/performance" 
              element={
                <ProtectedRoute>
                  <AdminPerformance />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/verarbeitungsverzeichnis" 
              element={
                <ProtectedRoute>
                  <AdminVerarbeitungsverzeichnis />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/auftragsverarbeitung" 
              element={
                <ProtectedRoute>
                  <AdminAuftragsverarbeitung />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/betroffenenrechte" 
              element={
                <ProtectedRoute>
                  <AdminBetroffenenrechte />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/backup" 
              element={
                <ProtectedRoute>
                  <AdminBackup />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/iso27001" 
              element={
                <ProtectedRoute>
                  <AdminISO27001 />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/changelog" 
              element={
                <ProtectedRoute>
                  <AdminChangelog />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/handbuch"
              element={
                <ProtectedRoute>
                  <AdminHandbuch />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/rechtstexte"
              element={
                <ProtectedRoute>
                  <AdminRechtstexte />
                </ProtectedRoute>
              }
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </LanguageProvider>
</QueryClientProvider>
);

export default App;
